import { Injectable, inject } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth, 
  signInWithCustomToken, 
  signOut, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { 
  getDatabase, 
  Database, 
  ref, 
  set, 
  onValue, 
  off,
  DataSnapshot,
  DatabaseReference,
  goOffline,
  goOnline
} from 'firebase/database';
import { Observable, BehaviorSubject } from 'rxjs';
import { UtilsService } from './utils.service';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private db: Database | null = null;
  private currentUser$ = new BehaviorSubject<User | null>(null);
  private listeners: Map<string, DatabaseReference> = new Map();
  private utils = inject(UtilsService);

  // Initialize Firebase with the appropriate environment config
  public initialize(): void {
    if (this.app) {
      this.utils.logDebug('Firebase already initialized');
      return;
    }

    try {
      this.app = initializeApp(environment.firebase);
      this.auth = getAuth(this.app);
      this.db = getDatabase(this.app);

      // Enable offline persistence for Firebase Realtime Database
      // This will cache data locally and sync when online
      if (this.db) {
        // Persistence is enabled by default in Firebase v9+
        this.utils.logInfo('Firebase initialized with offline persistence');
      }

      // Set up auth state listener
      if (this.auth) {
        onAuthStateChanged(this.auth, (user) => {
          this.currentUser$.next(user);
          if (user) {
            this.utils.logDebug(`Firebase auth state changed: user ${user.uid} logged in`);
          } else {
            this.utils.logDebug('Firebase auth state changed: user logged out');
          }
        });
      }
    } catch (error) {
      this.utils.logError('Failed to initialize Firebase', error);
      this.utils.handleError(error);
      throw error;
    }
  }

  // Sign in with custom token from backend
  public signInWithCustomToken(token: string): Promise<User> {
    if (!this.auth) {
      return Promise.reject('Firebase auth not initialized');
    }

    return signInWithCustomToken(this.auth, token)
      .then(userCredential => {
        this.utils.logInfo('User signed into Firebase');
        return userCredential.user;
      })
      .catch(error => {
        this.utils.logError('Firebase sign in failed', error);
      this.utils.handleError(error);
        throw error;
      });
  }

  // Sign out from Firebase
  public signOut(): Promise<void> {
    if (!this.auth) {
      return Promise.resolve();
    }

    // Clean up all listeners before signing out
    this.removeAllListeners();

    return signOut(this.auth)
      .then(() => {
        this.utils.logInfo('User signed out from Firebase');
      })
      .catch(error => {
        this.utils.logError('Firebase sign out failed', error);
      this.utils.handleError(error);
        throw error;
      });
  }

  // Get current user observable
  public getCurrentUser(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  // Get current user synchronously
  public getCurrentUserSync(): User | null {
    return this.auth?.currentUser || null;
  }

  // Get current user's ID token for security validation
  public async getCurrentUserIdToken(): Promise<string | null> {
    const user = this.getCurrentUserSync();
    if (!user) {
      return null;
    }
    
    try {
      return await user.getIdToken();
    } catch (error) {
      this.utils.logError('Failed to get current user ID token', error);
      return null;
    }
  }

  // Set data at a specific path
  public setData(path: string, data: any): Promise<void> {
    if (!this.db) {
      return Promise.reject('Firebase database not initialized');
    }

    const dbRef = ref(this.db, path);
    return set(dbRef, data)
      .then(() => {
        this.utils.logDebug(`Data set at path: ${path}`);
      })
      .catch(error => {
        this.utils.logError(`Failed to set data at path: ${path}`, error);
        this.utils.handleError(error);
        throw error;
      });
  }

  // Listen to data changes at a specific path
  public listenToData(path: string, callback: (data: any) => void): () => void {
    if (!this.db) {
      this.utils.logError('Firebase database not initialized');
      return () => {};
    }

    const dbRef = ref(this.db, path);
    
    // Store reference for cleanup
    this.listeners.set(path, dbRef);

    // Set up listener
    onValue(dbRef,
      (snapshot: DataSnapshot) => {
        const data = snapshot.val();
        callback(data);
      },
      (error) => {
        this.utils.logError(`Error listening to path: ${path}`, error);
        this.utils.handleError(error);
      }
    );

    // Return unsubscribe function
    return () => {
      if (this.db && this.listeners.has(path)) {
        const ref = this.listeners.get(path);
        if (ref) {
          off(ref);
          this.listeners.delete(path);
          this.utils.logDebug(`Listener removed for path: ${path}`);
        }
      }
    };
  }

  // Get data once from a specific path
  public getDataOnce(path: string): Promise<any> {
    if (!this.db) {
      return Promise.reject('Firebase database not initialized');
    }

    return new Promise((resolve, reject) => {
      const dbRef = ref(this.db!, path);
      onValue(dbRef, 
        (snapshot: DataSnapshot) => {
          const data = snapshot.val();
          off(dbRef); // Remove listener after getting data once
          resolve(data);
        },
        (error) => {
          off(dbRef);
          reject(error);
        },
        { onlyOnce: true }
      );
    });
  }

  // Remove all active listeners
  public removeAllListeners(): void {
    this.listeners.forEach((ref, path) => {
      off(ref);
      this.utils.logDebug(`Listener removed for path: ${path}`);
    });
    this.listeners.clear();
  }

  // Go offline (for testing or battery saving)
  public goOffline(): void {
    if (this.db) {
      goOffline(this.db);
      this.utils.logInfo('Firebase database went offline');
    }
  }

  // Go online (reconnect after offline)
  public goOnline(): void {
    if (this.db) {
      goOnline(this.db);
      this.utils.logInfo('Firebase database went online');
    }
  }

  // Check if initialized
  public isInitialized(): boolean {
    return this.app !== null && this.auth !== null && this.db !== null;
  }

  // Get Firebase UID if authenticated
  public getFirebaseUid(): string | null {
    return this.auth?.currentUser?.uid || null;
  }
}